import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './App.css';
import './ReportTheme.css';
import TokenBar from './TokenBar';
import ReportBoxWhisker from './ReportBoxWhisker';
import ReportThumb from './ReportThumb';
import { usePageTracking } from './hooks/useAnalytics';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const fmtRate = (v, d = 1) => (v == null ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(d)}`);
const fmtDate = iso => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const watchTitle = w => [w.brand, w.model].filter(Boolean).join(' ') || 'Unnamed watch';

// Collection score = rated-runs-weighted mean of per-watch health scores.
function collectionScore(watches) {
  const scored = watches.filter(w => w.health && w.health.score != null);
  if (!scored.length) return null;
  const tot = scored.reduce((s, w) => s + (w.rated_runs || 1), 0);
  return { score: Math.round(scored.reduce((s, w) => s + w.health.score * (w.rated_runs || 1), 0) / tot), n: scored.length };
}

function ScoreRing({ score, label }) {
  const R = 56, C = 2 * Math.PI * R, frac = Math.max(0, Math.min(100, score)) / 100;
  return (
    <div className="rpt-ring">
      <svg width="104" height="104" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r={R} fill="none" stroke="currentColor" strokeOpacity="0.14" strokeWidth="9" />
        <circle cx="65" cy="65" r={R} fill="none" stroke="var(--rpt-brass)" strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${(C * frac).toFixed(1)} ${C.toFixed(1)}`} transform="rotate(-90 65 65)" />
        <text x="65" y="62" textAnchor="middle" fontSize="34" fontWeight="600" fill="currentColor" fontFamily="Georgia, serif">{score}</text>
        <text x="65" y="82" textAnchor="middle" fontSize="9" letterSpacing="1.5" fill="currentColor" opacity="0.5">{label}</text>
      </svg>
    </div>
  );
}

export default function CollectionReportPage({ demo = false }) {
  const { userId, token } = useParams();
  const [search] = useSearchParams();
  // ?embed=1: opened inside the app's report browser — drop the web-only demo bar.
  const embed = search.get('embed') === '1';
  const [state, setState] = useState({ status: 'loading', report: null });
  const watchPath = id =>
    demo ? `/report-example/w/${id}` : token ? `/r/${token}/w/${id}` : `/analytics/${userId}/${id}`;
  usePageTracking(demo ? 'example_report' : 'collection_report', { page_title: 'ChronoLog Collection Report' });

  useEffect(() => {
    let cancelled = false;
    if (demo) {
      // Public example — synthetic fixture, no fetch, indexable.
      import('./exampleReport').then(m => {
        if (!cancelled) setState({ status: 'ready', report: m.EXAMPLE_COLLECTION });
      });
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const rpc = token ? 'get_report_by_token' : 'get_collection_report';
        const body = token ? { p_token: token } : { p_user_id: userId };
        const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${rpc}`, {
          method: 'POST',
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const report = await res.json();
        if (!cancelled) setState(report ? { status: 'ready', report } : { status: 'notfound', report: null });
      } catch { if (!cancelled) setState({ status: 'error', report: null }); }
    })();
    return () => { cancelled = true; };
  }, [userId, token, demo]);

  const { report } = state;
  const cs = useMemo(() => (report ? collectionScore(report.watches) : null), [report]);

  if (state.status === 'loading')
    return <div className="rpt-page"><main className="rpt-main"><div className="rpt-status">Loading…</div></main></div>;

  if (state.status === 'notfound' || state.status === 'error') {
    return (
      <div className="rpt-page">
        <Helmet><title>Collection Report — ChronoLog</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <main className="rpt-main"><div className="rpt-status">
          <h1 className="rpt-serif">Report unavailable</h1>
          <p>{token
            ? 'This link may have expired, point to a single watch, or have comparisons turned off. Open the report again from the ChronoLog app.'
            : 'This link doesn’t match a synced collection, or comparisons are turned off.'}</p>
        </div></main>
      </div>
    );
  }

  const { totals, comparisons } = report;

  return (
    <div className="rpt-page">
      <Helmet>
        <title>{demo ? 'Example Watch Accuracy Report — ChronoLog' : 'Collection Report — ChronoLog'}</title>
        {!demo && <meta name="robots" content="noindex, nofollow" />}
      </Helmet>
      <main className="rpt-main">
        {demo && !embed && (
          <div className="rpt-demobar">
            Example report with sample data — ChronoLog builds yours from your real watches.
            {' '}<Link to="/accuracy-reports">How reports work</Link> · <a href="/">Get the app</a>
          </div>
        )}
        {token && report._token && <TokenBar meta={report._token} />}

        <div className="rpt-brandline">CHRONOLOG</div>
        {/* In-ring label stays short ("HEALTH") — longer strings overflow the
            130-unit viewBox and clip; the full context lives on the line below. */}
        {cs && <ScoreRing score={cs.score} label="HEALTH" />}
        <div className="rpt-ringlbl">
          {cs ? `COLLECTION SCORE · ${totals.eligible_watches} OF ${totals.watches} WATCHES RATED`
              : `${totals.watches} ${totals.watches === 1 ? 'WATCH' : 'WATCHES'}`}
        </div>
        <p className="rpt-freshness">same order as your Watchbox · last synced {fmtDate(report.last_synced_at)}</p>

        <div className="rpt-watchlist">
          {report.watches.map(w => {
            const rated = w.weighted_rate_spd != null;
            const eligible = w.health && w.health.score != null;
            return (
              <Link key={w.id} to={watchPath(w.id)} className="rpt-wcard">
                <div className="rpt-wtop">
                  <ReportThumb token={token} watchId={w.id} hasPhoto={w.has_photo} photoUrl={w.photo_url} />
                  <div className="rpt-wname">
                    <div className="b">{watchTitle(w)}</div>
                    <div className="m">{[w.caliber && `Cal. ${w.caliber}`, rated && `${w.rated_runs} ${w.rated_runs === 1 ? 'run' : 'runs'}`, w.since_service_at && `since service ${fmtDate(w.since_service_at)}`].filter(Boolean).join(' · ') || 'not timed yet'}</div>
                  </div>
                  {rated && <div className="rpt-wrate">{fmtRate(w.weighted_rate_spd)} <span>s/d</span></div>}
                  {eligible && <div className={`rpt-scorechip t-${w.health.score >= 70 ? 'good' : w.health.score >= 50 ? 'mid' : 'warn'}`}>{w.health.score}</div>}
                  <div className="rpt-chev">›</div>
                </div>
                {w.benchmark && w.benchmark.box ? (
                  <div className="rpt-spreadrow">
                    <span className="rpt-sl">{w.benchmark.cohort_label.toUpperCase()}</span>
                    <span className="rpt-boxmini"><ReportBoxWhisker box={w.benchmark.box} you={w.benchmark.this_rate} height={20} /></span>
                    <span className="rpt-sl">{w.benchmark.bucket} timepieces</span>
                  </div>
                ) : rated && !eligible ? (
                  <div className="rpt-spreadrow rpt-muted">keep timing to unlock a health score — {Math.max(0, 6 - (w.meas_total || 0))} more measurements</div>
                ) : null}
              </Link>
            );
          })}
        </div>

        {comparisons && comparisons.collections_bucket && (
          <div className="rpt-community">
            👥 Compared against <b>{comparisons.collections_bucket}</b> ChronoLog collections, anonymously
            {comparisons.accuracy_percentile != null && <> — your collection is more accurate than <b>{comparisons.accuracy_percentile}%</b></>}.
          </div>
        )}

        <footer className="rpt-footer">
          Measured with <a href="/">ChronoLog</a> — the watch accuracy app.{' '}
          <Link to="/accuracy-reports">How reports work</Link>. Reflects this collection’s last sync.
        </footer>
      </main>
    </div>
  );
}
